// chat-search.ts — pure helpers behind the ⌘K chat-search dialog
// (SearchDialog.svelte). The manager does the actual full-text search
// (/sessions/search → api.searchSessions); these helpers turn its answer and a
// replayed transcript (history-fold) into what the dialog renders: highlighted
// text segments, a plain-text preview of a chat, and the row the preview
// should scroll to. Kept free of Svelte so they're unit-testable.

import type { FeedMessage } from './history-fold';
import type { SessionRow } from './sessions-store.svelte';
import type { SessionSearchResult, SessionSearchSnippet } from './api';

// Same tokenisation as the manager (session-search.ts queryTerms): lowercase,
// whitespace-split, de-duplicated. Mirrored client-side so highlighting agrees
// with what the server matched.
export function queryTerms(raw: string): string[] {
	const q = raw.trim().slice(0, 200).toLowerCase();
	if (!q) return [];
	const out: string[] = [];
	for (const t of q.split(/\s+/)) {
		if (t && !out.includes(t)) out.push(t);
		if (out.length >= 8) break;
	}
	return out;
}

export interface Segment {
	text: string;
	hit: boolean;
}

// Split `text` into alternating plain / hit segments so the template can wrap
// hits in <mark>. Case-insensitive; overlapping term hits merge into one hit
// segment; longer terms win when two start at the same offset.
export function highlightSegments(text: string, terms: string[]): Segment[] {
	if (!text) return [];
	if (terms.length === 0) return [{ text, hit: false }];
	const lower = text.toLowerCase();
	// Collect [start,end) ranges for every occurrence of every term.
	const ranges: Array<[number, number]> = [];
	for (const t of terms) {
		if (!t) continue;
		let from = 0;
		for (;;) {
			const i = lower.indexOf(t, from);
			if (i === -1) break;
			ranges.push([i, i + t.length]);
			from = i + 1;
		}
	}
	if (ranges.length === 0) return [{ text, hit: false }];
	ranges.sort((a, b) => a[0] - b[0] || b[1] - a[1]);
	// Merge overlaps.
	const merged: Array<[number, number]> = [];
	for (const r of ranges) {
		const last = merged[merged.length - 1];
		if (last && r[0] <= last[1]) last[1] = Math.max(last[1], r[1]);
		else merged.push([r[0], r[1]]);
	}
	const out: Segment[] = [];
	let cursor = 0;
	for (const [s, e] of merged) {
		if (s > cursor) out.push({ text: text.slice(cursor, s), hit: false });
		out.push({ text: text.slice(s, e), hit: true });
		cursor = e;
	}
	if (cursor < text.length) out.push({ text: text.slice(cursor), hit: false });
	return out;
}

// Lossy markdown → plain text for search excerpts and the preview pane. The
// assistant answers in markdown; showing '**€48,210**' / '### Highlights' /
// table pipes verbatim reads as noise in a one-line excerpt. Only the common
// block + inline markers are stripped (fences, headings, quotes, tables,
// bold, inline code, links); anything else passes through untouched.
// MIRROR of backend/manager/src/session-search.ts plainText — keep identical.
export function plainText(md: string): string {
	return md
		.replace(/^[ \t]*```[^\n]*(\n|$)/gm, '') // code fence lines (whole line)
		.replace(/^[ \t]*#{1,6}[ \t]+/gm, '') // headings
		.replace(/^[ \t]*>[ \t]?/gm, '') // blockquotes
		.replace(/^[ \t]*\|?[ \t]*:?-{2,}:?[ \t]*(\|[ \t]*:?-{2,}:?[ \t]*)+\|?[ \t]*(\n|$)/gm, '') // table separator rows (whole line)
		.replace(/^[ \t]*\|[ \t]*(.*?)[ \t]*\|[ \t]*$/gm, (_m, cells: string) => cells.split(/[ \t]*\|[ \t]*/).join(' · ')) // table rows
		.replace(/(\*\*|__)(.+?)\1/g, '$2') // bold
		.replace(/`([^`\n]+)`/g, '$1') // inline code
		.replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1') // images → alt
		.replace(/\[([^\]]+)\]\([^)]*\)/g, '$1') // links → label
		.replace(/\n{3,}/g, '\n\n')
		.trim();
}

export interface PreviewRow {
	role: 'user' | 'assistant';
	text: string;
	timestamp: number;
	/** True when the row contains every query term (what the server counted). */
	hit: boolean;
}

export const PREVIEW_ROW_MAX_CHARS = 1200;

export interface TranscriptRow {
	role: 'user' | 'assistant';
	text: string;
	timestamp: number;
}

// Reduce a replayed transcript to plain-text rows: user messages and the
// assistant's text blocks (thinking + tool I/O are the agent's internals, not
// the conversation). Same selection as the manager's searchableMessages, so
// the client-side fallback search and the preview agree with the server.
export function transcriptRows(messages: FeedMessage[]): TranscriptRow[] {
	const rows: TranscriptRow[] = [];
	for (const m of messages) {
		if (m.role === 'user') {
			if (m.content.trim()) rows.push({ role: 'user', text: m.content, timestamp: m.timestamp });
		} else if (m.role === 'assistant') {
			const text = plainText(
				m.content
					.filter((b): b is { type: 'text'; text: string } => b.type === 'text')
					.map((b) => b.text)
					.join('\n'),
			);
			if (text) rows.push({ role: 'assistant', text, timestamp: m.timestamp });
		}
	}
	return rows;
}

// The preview pane's rows: long rows are cut so one giant answer can't
// dominate the pane; the cut keeps the FIRST hit visible.
export function previewRows(messages: FeedMessage[], terms: string[]): PreviewRow[] {
	return transcriptRows(messages).map((r) => ({
		role: r.role,
		text: clampAroundHit(r.text, terms),
		timestamp: r.timestamp,
		hit: matchesAll(r.text, terms),
	}));
}

// AND semantics within one text: every term must appear (case-insensitive).
// Mirror of the manager's matchesAll.
export function matchesAll(text: string, terms: string[]): boolean {
	if (terms.length === 0) return false;
	const lower = text.toLowerCase();
	return terms.every((t) => lower.includes(t));
}

export const SNIPPET_RADIUS = 80;

// Excerpt ±radius chars around the FIRST occurrence of any term, cut on word
// boundaries where possible, with "…" marking the cuts. Newlines collapse to
// spaces so a snippet stays one line. Mirror of the manager's excerpt.
export function excerpt(text: string, terms: string[], radius: number = SNIPPET_RADIUS): string {
	const flat = text.replace(/\s+/g, ' ').trim();
	const lower = flat.toLowerCase();
	let hit = -1;
	let hitLen = 0;
	for (const t of terms) {
		const i = lower.indexOf(t);
		if (i !== -1 && (hit === -1 || i < hit)) {
			hit = i;
			hitLen = t.length;
		}
	}
	if (hit === -1) return flat.length > radius * 2 ? flat.slice(0, radius * 2).trimEnd() + '…' : flat;
	let start = Math.max(0, hit - radius);
	let end = Math.min(flat.length, hit + hitLen + radius);
	if (start > 0) {
		const sp = flat.lastIndexOf(' ', start);
		if (sp !== -1 && sp < hit && start - sp < 24) start = sp + 1;
	}
	if (end < flat.length) {
		const sp = flat.indexOf(' ', end);
		if (sp !== -1 && sp > hit + hitLen && sp - end < 24) end = sp;
	}
	return (start > 0 ? '…' : '') + flat.slice(start, end) + (end < flat.length ? '…' : '');
}

export const SNIPPETS_PER_SESSION = 3;

// Match one chat (title + transcript) against the terms — the manager's
// searchOne, run in the browser. Returns null when nothing matches.
export function searchOneLocal(
	meta: { sessionId: string; name?: string; createdAt: number; lastActivityAt: number },
	messages: FeedMessage[],
	terms: string[],
): SessionSearchResult | null {
	if (terms.length === 0) return null;
	const name = meta.name?.trim() ? meta.name.trim() : null;
	const titleMatch = name !== null && matchesAll(name, terms);
	const snippets: SessionSearchSnippet[] = [];
	let matchCount = 0;
	for (const row of transcriptRows(messages)) {
		if (!matchesAll(row.text, terms)) continue;
		matchCount += 1;
		if (snippets.length < SNIPPETS_PER_SESSION) {
			snippets.push({ role: row.role, at: row.timestamp, text: excerpt(row.text, terms) });
		}
	}
	if (!titleMatch && matchCount === 0) return null;
	return {
		id: meta.sessionId,
		name,
		createdAt: meta.createdAt,
		lastActivityAt: meta.lastActivityAt,
		titleMatch,
		matchCount,
		snippets,
	};
}

// How many chats the browser-side fallback is willing to pull transcripts for.
// A frontend can be deployed minutes before the manager that serves
// /sessions/search (the two apps have separate Coolify watch paths), and a
// local stack may run an older manager; rather than a dead search box the
// dialog then searches the most recent chats itself through the existing
// per-chat /messages endpoint. Bounded so it can't pull hundreds of transcripts.
export const LOCAL_SEARCH_MAX_CHATS = 100;
export const LOCAL_SEARCH_CONCURRENCY = 4;

export interface LocalSearchOutcome {
	results: SessionSearchResult[];
	/** Number of chats that were NOT searched because of the cap. */
	skipped: number;
}

// Browser-side search over the caller's own chats. `load` fetches (and should
// cache) a chat's replayed transcript; failures for one chat are skipped so a
// single bad log can't blank the whole search. Honors `signal` between chats.
export async function searchLocally(
	sessions: SessionRow[],
	terms: string[],
	load: (sessionId: string) => Promise<FeedMessage[]>,
	signal?: AbortSignal,
	max: number = LOCAL_SEARCH_MAX_CHATS,
	concurrency: number = LOCAL_SEARCH_CONCURRENCY,
): Promise<LocalSearchOutcome> {
	const live = [...sessions].filter((s) => !s.deletedAt).sort((a, b) => b.lastActivityAt - a.lastActivityAt);
	const candidates = live.slice(0, max);
	const results: SessionSearchResult[] = [];
	if (terms.length === 0) return { results, skipped: 0 };
	let next = 0;
	const worker = async (): Promise<void> => {
		for (;;) {
			if (signal?.aborted) return;
			const i = next++;
			const s = candidates[i];
			if (!s) return;
			let messages: FeedMessage[];
			try {
				messages = await load(s.sessionId);
			} catch {
				continue;
			}
			const hit = searchOneLocal(s, messages, terms);
			if (hit) results.push(hit);
		}
	};
	await Promise.all(Array.from({ length: Math.max(1, concurrency) }, () => worker()));
	results.sort((a, b) => b.lastActivityAt - a.lastActivityAt);
	return { results, skipped: live.length - candidates.length };
}

// Keep a row to PREVIEW_ROW_MAX_CHARS, windowed so the first term hit stays
// inside the kept span (a hit at char 5000 of a long answer would otherwise be
// cut away and the preview would show no highlight for a counted match).
export function clampAroundHit(text: string, terms: string[], max: number = PREVIEW_ROW_MAX_CHARS): string {
	if (text.length <= max) return text;
	const lower = text.toLowerCase();
	let first = -1;
	for (const t of terms) {
		const i = lower.indexOf(t);
		if (i !== -1 && (first === -1 || i < first)) first = i;
	}
	if (first === -1 || first < max - 100) return text.slice(0, max).trimEnd() + '…';
	const start = Math.max(0, first - Math.floor(max / 2));
	const end = Math.min(text.length, start + max);
	return '…' + text.slice(start, end).trim() + (end < text.length ? '…' : '');
}

// Index of the preview row the pane should scroll to when it opens: the first
// row that contains every term, else -1 (title-only match → stay at the top).
export function firstHitIndex(rows: PreviewRow[]): number {
	return rows.findIndex((r) => r.hit);
}

// One row in the dialog's left-hand list. With an EMPTY query the list shows
// the sidebar's own recent chats (no request) so the dialog is useful the
// moment it opens — mirroring the sidebar order (recency, trashed hidden).
export interface SearchListItem {
	id: string;
	title: string;
	lastActivityAt: number;
	snippet: { role: 'user' | 'assistant'; text: string } | null;
	matchCount: number;
	titleMatch: boolean;
}

export const RECENT_MAX = 30;

export function recentItems(sessions: SessionRow[], max: number = RECENT_MAX, hidden: (id: string) => boolean = () => false): SearchListItem[] {
	return [...sessions]
		.filter((s) => !s.deletedAt && !hidden(s.sessionId))
		.sort((a, b) => b.lastActivityAt - a.lastActivityAt)
		.slice(0, max)
		.map((s) => ({
			id: s.sessionId,
			title: s.name?.trim() || 'Untitled chat',
			lastActivityAt: s.lastActivityAt,
			snippet: null,
			matchCount: 0,
			titleMatch: false,
		}));
}

export function resultItems(results: SessionSearchResult[]): SearchListItem[] {
	return results.map((r) => ({
		id: r.id,
		title: r.name?.trim() || 'Untitled chat',
		lastActivityAt: r.lastActivityAt,
		snippet: r.snippets[0] ? { role: r.snippets[0].role, text: r.snippets[0].text } : null,
		matchCount: r.matchCount,
		titleMatch: r.titleMatch,
	}));
}

// Keyboard navigation over the list: ↑/↓ wrap around; anything else leaves
// the index untouched. -1 (nothing selected) steps to the first / last row.
export function stepSelection(current: number, delta: 1 | -1, length: number): number {
	if (length === 0) return -1;
	if (current < 0) return delta === 1 ? 0 : length - 1;
	return (current + delta + length) % length;
}
